import { Award, MapPin, Plane, Radio, BookOpen } from "lucide-react";
import { TID } from "@/lib/testIds";

const BIO_IMG =
  "https://customer-assets.emergentagent.com/job_aviation-integrity/artifacts/4vtfk6ah_Gemini_Generated_Image_%20%285%29.png";

const credentials = [
  { icon: Radio, label: "FAA Telecom Ops Analyst" },
  { icon: Plane, label: "Licensed Commercial Pilot" },
  { icon: BookOpen, label: "Drone Pilot & Educator" },
  { icon: MapPin, label: "Independent · Henry County, GA" },
];

const AboutImage = () => (
  <div className="lg:col-span-5 order-2 lg:order-1">
    <div className="relative corner-marks text-[var(--navy)]">
      <div className="absolute -top-3 left-0 font-mono text-[10px] uppercase tracking-widest text-[var(--navy)]/50">
        FILE-002 / BIO
      </div>
      <div className="absolute -top-3 right-0 font-mono text-[10px] uppercase tracking-widest text-[var(--navy)]/50">
        EST. 2017 — HENRY COUNTY, GA
      </div>
      <div className="relative aspect-[4/5] overflow-hidden bg-[var(--paper-soft)] border border-[var(--line)]">
        <img
          src={BIO_IMG}
          alt="Victor Appiah portrait"
          data-testid={TID.aboutImage}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    </div>

    <div className="mt-6 grid grid-cols-2 gap-3">
      {credentials.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.label}
            className="flex items-center gap-3 border border-[var(--line)] bg-white px-4 py-3"
          >
            <Icon className="w-4 h-4 text-[var(--accent)] shrink-0" />
            <span className="text-xs font-semibold text-[var(--navy)] uppercase tracking-wider">
              {c.label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const AboutContent = () => (
  <div className="lg:col-span-7 order-1 lg:order-2">
    <div className="label-eyebrow mb-4">04 — About the Host</div>
    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] leading-[0.95]">
      Meet Victor <span className="text-[var(--accent)]">Appiah.</span>
    </h2>

    <div className="mt-8 space-y-6 text-base md:text-lg text-[var(--navy-soft)] leading-relaxed max-w-2xl">
      <p>
        A Henry County, GA resident since{" "}
        <span className="text-[var(--navy)] font-semibold">2017</span>,
        Vic is an FAA Telecommunications Operations Analyst, licensed
        commercial pilot, drone educator, and the independent voice
        behind the{" "}
        <span className="text-[var(--navy)] font-semibold">
          ATFQ (Answer The F**king Question)
        </span>{" "}
        watchdog podcast.
      </p>
      <p>
        In 2026 he ran as an independent for the Henry County Board
        of Commissioners, District 5 — a campaign that ended at the
        ballot-access signature threshold and became the founding
        case study for ATFQ&apos;s investigation of institutional
        gatekeeping. He now channels that same aviation-grade rigor
        into on-mic accountability journalism for District 5 and
        beyond.
      </p>
    </div>

    <div className="mt-10 border-t border-[var(--line)] pt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
      <div>
        <div className="font-display text-3xl text-[var(--navy)]">
          10+
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-[var(--navy)]/50 mt-1">
          Years in Henry County
        </div>
      </div>
      <div>
        <div className="font-display text-3xl text-[var(--navy)]">
          FAA
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-[var(--navy)]/50 mt-1">
          Critical Infrastructure
        </div>
      </div>
      <div>
        <div className="font-display text-3xl text-[var(--navy)]">
          Part 107
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-[var(--navy)]/50 mt-1">
          Drone Pilot & Educator
        </div>
      </div>
    </div>

    <div className="mt-10 inline-flex items-center gap-3 border border-[var(--line)] px-5 py-3 bg-[var(--paper-soft)]">
      <Award className="w-4 h-4 text-[var(--accent)]" />
      <span className="text-xs font-bold uppercase tracking-widest text-[var(--navy)]">
        Resilience · Transparency · Duty
      </span>
    </div>
  </div>
);

export const About = () => {
  return (
    <section
      id="about"
      data-testid={TID.aboutSection}
      className="relative bg-white py-20 md:py-32"
    >
      <div className="px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <AboutImage />
          <AboutContent />
        </div>
      </div>
    </section>
  );
};

export default About;
